/* eslint-disable react/prop-types */
import React from 'react';
import classNames from 'classnames';
import Style from './index.less';
import { CursorManagerEventType, ICursorTagsProps } from '@src/slide/application/service/cursor-manager/core/cursor-manager.interface';

interface ICursorTagProps {
    x: number;
    y: number;
    color: string;
    text: string;
    visible: boolean;
    duration: number;
    onHide?: () => void;
}

export class CursorTag extends React.Component<ICursorTagProps, { visible: boolean }> {
    private timer: ReturnType<typeof setTimeout> | null;

    public constructor(props: ICursorTagProps) {
        super(props);
        this.state = {
            visible: true,
        };
    }

    public componentDidMount() {
        this.updateTimer(this.props.duration);
    }

    /* eslint-disable camelcase */
    public UNSAFE_componentWillReceiveProps(nextProps: ICursorTagProps) {
        /* eslint-enable camelcase */
        if (nextProps.duration !== this.props.duration) {
            this.updateTimer(nextProps.duration);
        }
    }

    public componentWillUnmount() {
        this.timer && clearTimeout(this.timer);
        this.timer = null;
    }

    public render() {
        const {
            x = 0,
            y = 0,
            color = 'white',
            text = '',
        } = this.props;
        let visible = false;
        if (this.autoHide) {
            ({ visible } = this.state);
        } else {
            ({ visible } = this.props);
        }
        return (
            <div
                className={classNames({
                    [Style['cursor-tag']]: true,
                    [Style['fade-in']]: visible,
                    [Style['fade-out']]: !visible,
                })}
                style={{
                    left: `${x}px`,
                    top: `${y}px`,
                    backgroundColor: color,
                }}
                onAnimationEnd={this.handleAnimationEnd}
            >{text}</div>
        );
    }

    private updateTimer(duration = 0) {
        if (duration !== 0) {
            this.setState({ visible: true });
            this.timer && clearTimeout(this.timer);
            this.timer = setTimeout(() => {
                this.setState({ visible: false });
            }, duration);
        } else {
            this.timer && clearTimeout(this.timer);
            this.timer = null;
        }
    }

    private get autoHide() {
        return this.props.duration !== 0;
    }

    private handleAnimationEnd = () => {
        let visible = false;
        if (this.autoHide) {
            ({ visible } = this.state);
        } else {
            ({ visible } = this.props);
        }
        if (!visible) {
            this.props.onHide?.();
        }
    };
}

class CursorTags extends React.Component<ICursorTagsProps, { tags: any }> {
    private tags: Record<any, any>;
    private autoHideTimers: any;

    public constructor(props: ICursorTagsProps) {
        super(props);
        this.tags = {};
        this.autoHideTimers = {};
        this.state = {
            tags: {},
        };
    }

    public componentDidMount() {
        this.props.cursorManager.on(CursorManagerEventType.REFRESH, this.handleRefreshCursorTags);
        this.props.cursorManager.on(CursorManagerEventType.CLEAR, this.handleClearCursorTags);
        this.props.cursorManager.on(CursorManagerEventType.SHOW, this.handleShowCursorTag);
        this.props.cursorManager.on(CursorManagerEventType.HIDE, this.handleHideCursorTag);
    }

    public componentWillUnmount() {
        this.props.cursorManager.off(CursorManagerEventType.REFRESH, this.handleRefreshCursorTags);
        this.props.cursorManager.off(CursorManagerEventType.CLEAR, this.handleClearCursorTags);
        this.props.cursorManager.off(CursorManagerEventType.SHOW, this.handleShowCursorTag);
        this.props.cursorManager.off(CursorManagerEventType.HIDE, this.handleHideCursorTag);
    }

    public render() {
        const { scale, top, left } = this.props;
        const { tags } = this.state;
        return (
            <div>
                {Object.keys(tags).sort()
                    .map((key) => {
                        const tag = tags[key];
                        return (
                            <CursorTag
                                key={key}
                                x={(tag.x * scale) + left}
                                y={(tag.y * scale) + top}
                                color={tag.color}
                                text={tag.name}
                                visible={tag.visible}
                                duration={tag.duration}
                                onHide={this.handleCursorTagHide(key)}
                            />
                        );
                    })}
            </div>
        );
    }

    /**
     * 处理光标刷新时tag展示，收到协同光标变化的时候触发
     */
    private handleRefreshCursorTags = (obj) => {
        const newTags = { ...this.tags };
        obj.show.forEach((tag) => {
            if (!newTags[tag.key]) {
                newTags[tag.key] = {
                    x: tag.position.x,
                    y: tag.position.y,
                    uid: tag.cursor.userId,
                    name: tag.cursor.name,
                    color: tag.cursor.color,
                    visible: true,
                    duration: 3000,
                };
            }
        });
        obj.hide.forEach((tag) => {
            delete newTags[tag.key];
        });
        this.tags = newTags;
        this.setState({
            tags: newTags,
        });
    };

    /**
     * 清空光标tag，若有元素key列表，则清空指定元素的，否则清空所有。选中元素时有元素的key列表
     */
    private handleClearCursorTags = (obj) => {
        const { keys } = obj;
        if (keys && keys.length > 0) {
            const newTags = { ...this.tags };
            keys.forEach((key) => {
                delete newTags[key];
            });
            this.tags = newTags;
            this.setState({
                tags: newTags,
            });
        } else {
            this.tags = {};
            this.setState({
                tags: {},
            });
        }
    };

    /**
     * 显示指定元素的光标tag，设定时自动隐藏timer会清除
     */
    private handleShowCursorTag = (obj) => {
        const newTags = { ...this.tags };
        newTags[obj.key] = {
            x: obj.position.x,
            y: obj.position.y,
            uid: obj.cursor.userId,
            name: obj.cursor.name,
            color: obj.cursor.color,
            visible: true,
            duration: 0,
        };
        this.tags = newTags;
        this.setState({
            tags: newTags,
        });
    };

    /**
     * 隐藏光标tag，触发后1s后消失
     */
    private handleHideCursorTag = (obj) => {
        const newTags = { ...this.tags };
        const tag = newTags[obj.key];
        if (tag) {
            tag.visible = false;
            tag.duration = 1000;
        }
        this.tags = newTags;
        this.setState({
            tags: newTags,
        });
    };

    /**
     * 删除光标tag，立即消失
     */
    private deleteCursorTag = (key) => {
        const newTags = { ...this.tags };
        delete newTags[key];
        this.tags = newTags;
        this.setState({
            tags: newTags,
        });
    };

    /**
     * 光标tag消失动画结束后，删除tag
     */
    private handleCursorTagHide = key => () => {
        this.deleteCursorTag(key);
    };
}

export default CursorTags;
